import { AddImageIcon, CentralIcon } from "~/components/icons/central-icons";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import type { ReactNode } from "react";

type ProfileIdentityHeaderProps = {
  name: string;
  nameContent?: ReactNode;
  imageUrl: string | null;
  avatarLoading?: boolean;
  nameLoading?: boolean;
  fallbackImageUrl?: string;
  headingAs?: "h1" | "p";
  className?: string;
  editAvatar?: {
    label: string;
    disabled: boolean;
    busy: boolean;
    onClick: () => void;
  };
};

function initials(name: string) {
  const cleaned = name.trim().replace(/^@/u, "");
  return cleaned.startsWith("0x")
    ? "0x"
    : cleaned.slice(0, 2).toUpperCase() || "U";
}

/** One responsive identity treatment for the public profile and its settings. */
export function ProfileIdentityHeader({
  name,
  nameContent,
  imageUrl,
  avatarLoading = false,
  nameLoading = false,
  fallbackImageUrl,
  headingAs: Heading = "p",
  className,
  editAvatar,
}: ProfileIdentityHeaderProps) {
  const avatar = (
    <Avatar key={imageUrl} className="size-14 bg-surface-2 sm:size-20">
      {imageUrl && !avatarLoading ? (
        <AvatarImage alt="" src={imageUrl} className="object-cover" />
      ) : null}
      <AvatarFallback
        className="bg-surface-2 text-title-sm font-medium text-foreground sm:text-title"
        render={(props, { imageLoadingStatus }) => (
          <span {...props}>
            {avatarLoading || (imageUrl && imageLoadingStatus !== "error") ? (
              <Skeleton className="size-full rounded-full motion-reduce:animate-none" />
            ) : fallbackImageUrl ? (
              <img
                src={fallbackImageUrl}
                alt=""
                className="size-full object-cover"
              />
            ) : (
              initials(name)
            )}
          </span>
        )}
      />
    </Avatar>
  );

  return (
    <div
      className={cn("flex min-w-0 items-start gap-3 sm:gap-6", className)}
      data-slot="profile-identity-header"
      aria-busy={avatarLoading || undefined}
    >
      {editAvatar ? (
        <div className="group/profile-avatar relative shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-14 overflow-visible rounded-full p-0 disabled:opacity-100 aria-disabled:opacity-100 sm:size-20"
            aria-label={editAvatar.label}
            aria-busy={editAvatar.busy || undefined}
            disabled={editAvatar.disabled}
            onClick={editAvatar.onClick}
          >
            {avatar}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-background/65 text-foreground group-has-[[data-slot=skeleton]]/profile-avatar:hidden"
            >
              <CentralIcon icon={AddImageIcon} className="size-8 sm:size-10" />
            </span>
          </Button>
        </div>
      ) : (
        avatar
      )}
      {nameLoading ? (
        <div className="flex min-h-14 items-center sm:min-h-20">
          <Skeleton className="h-7 w-40 sm:h-9" />
        </div>
      ) : (
        (nameContent ?? (
          <Heading className="flex min-h-14 min-w-0 items-center break-all text-title-sm font-medium text-foreground sm:min-h-20 sm:text-title">
            {name}
          </Heading>
        ))
      )}
    </div>
  );
}
